import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PlusCircle, ShoppingBag, Truck, Wallet, ChevronLeft, ChevronRight,
  Book,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { RootState } from "../../redux/store";
import logo from "../../assets/app/logo.png";

import Products              from "./Products";
import CommerceDeliveryZones from "./CommerceDeliveryZones";
import HostProductOrders     from "./HostProductOrders";
import Books from "./Books";

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same palette as AtlasHome / Products
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg:        "#FBF6EE",
  panelBg:   "#FFFFFF",
  headerBg:  "rgba(255,255,255,0.97)",
  surface:   "#FCEFD4",

  dark:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",

  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",

  border:    "#EEDFC4",
  divider:   "#F3E6CE",
  danger:    "#C8202E",
  success:   "#1FAA59",
};

const GRAD      = `linear-gradient(135deg, ${HEART.gold}, ${HEART.red})`;
const GRAD_H    = `linear-gradient(90deg, ${HEART.gold}, ${HEART.red})`;
const GRAD_RING = `linear-gradient(90deg, ${HEART.dark}, ${HEART.gold}, ${HEART.red}, ${HEART.gold}, ${HEART.dark})`;

const SIDEBAR_W           = 240;
const SIDEBAR_W_COLLAPSED = 72;
const TABBAR_H             = 64;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
  *,::before,::after{box-sizing:border-box;margin:0;padding:0;}
  body{background:${HEART.bg};}
  button,input{font-family:inherit;}
  ::-webkit-scrollbar{width:3px;}
  ::-webkit-scrollbar-thumb{background:${HEART.border};border-radius:99px;}

  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes pulseDot{0%,100%{opacity:1;}50%{opacity:.4;}}
  @keyframes fadeSlide{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}

  .sd-nav{
    width:100%;display:flex;align-items:center;gap:12px;
    padding:10px 12px;border:none;background:transparent;
    cursor:pointer;transition:background .15s;font-family:inherit;
    border-radius:12px;text-align:left;position:relative;z-index:1;
  }
  .sd-nav:hover{background:${HEART.goldSoft};}
  .sd-nav.on{
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
    box-shadow:0 6px 16px rgba(200,32,46,.32);
  }
  .sd-nav.collapsed{justify-content:center;padding:10px;}

  .sd-navicon{
    width:34px;height:34px;border-radius:10px;flex-shrink:0;
    background:${HEART.goldSoft};display:flex;align-items:center;justify-content:center;
    transition:background .15s,color .15s;
  }
  .sd-nav.on .sd-navicon{background:rgba(255,255,255,.18);}

  .sd-navlabel{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

  .sd-logout{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:10px 12px;border-radius:12px;color:${HEART.danger};font-weight:700;
    font-size:13px;text-align:left;transition:background .15s;font-family:inherit;
  }
  .sd-logout:hover{background:${HEART.redSoft};}
  .sd-logout.collapsed{justify-content:center;padding:10px;}

  .sd-collapse-toggle{
    display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:9px;
    background:${HEART.goldSoft};border:none;color:${HEART.redDark};cursor:pointer;flex-shrink:0;
    transition:background .15s,color .15s;
  }
  .sd-collapse-toggle:hover{background:${HEART.gold};color:#fff;}

  .sd-icon{
    width:34px;height:34px;border:1px solid transparent;
    background:transparent;display:flex;align-items:center;
    justify-content:center;cursor:pointer;transition:all .15s;
    border-radius:9px;
  }
  .sd-icon:hover{background:${HEART.goldSoft};border-color:rgba(200,32,46,.25);}

  .page-watermark{
    position:fixed;top:50%;left:50%;
    width:min(58vw,540px);height:min(58vw,540px);
    transform:translate(-50%,-50%);
    opacity:.035;pointer-events:none;z-index:0;object-fit:contain;
  }
  .sidebar-watermark{
    position:absolute;left:50%;bottom:70px;
    width:140px;height:140px;transform:translateX(-50%) rotate(-6deg);
    opacity:.045;pointer-events:none;z-index:0;object-fit:contain;
  }

  /* ── mobile bottom tab bar ── */
  .sd-tabbar{
    position:fixed;left:0;right:0;bottom:0;display:flex;
    background:${HEART.panelBg};border-top:1px solid ${HEART.border};
    z-index:997;padding-bottom:env(safe-area-inset-bottom);
    box-shadow:0 -4px 18px rgba(27,14,6,0.07);
  }
  .sd-tab{
    flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:3px;padding:8px 4px 10px;background:transparent;border:none;cursor:pointer;
    color:${HEART.textFaint};transition:color .15s;position:relative;
  }
  .sd-tab.active{color:${HEART.red};}
  .sd-tab span{font-size:10.5px;font-weight:700;font-family:'DM Sans',sans-serif;}
  .sd-tab-iconwrap{
    width:26px;height:26px;border-radius:8px;display:flex;align-items:center;justify-content:center;
    transition:background .15s;
  }
  .sd-tab.active .sd-tab-iconwrap{background:${HEART.goldSoft};}
  .sd-tab-dot{
    position:absolute;top:4px;width:4px;height:4px;border-radius:50%;
    background:${HEART.red};animation:pulseDot 2s infinite;
  }
`;

/* ── nav config ── */
type TabKey = "add" | "books" | "orders" | "delivery" | "payout";
const NAV: { id: TabKey; label: string; icon: React.ElementType }[] = [
  { id: "add",      label: "Products",       icon: PlusCircle  },
  { id: "books",      label: "Books",       icon:   Book  },
  { id: "orders",   label: "Orders",         icon: ShoppingBag },
  { id: "delivery", label: "Delivery Zones", icon: Truck       },
  { id: "payout",   label: "Payouts",        icon: Wallet      },
];

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const SellerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const params   = useParams<{ userId?: string }>();
  const authUser = useSelector((s: RootState) => s.auth.user);
  const userId   = params.userId || authUser?.userId;

  const [activeTab, setActiveTab] = useState<TabKey>("add");
  const [mobile,    setMobile]    = useState(window.innerWidth < 1024);
  const [collapsed, setCollapsed] = useState(false); // desktop sidebar collapse

  useEffect(() => {
    if (!authUser) { navigate("/login"); return; }
    if (!userId)   { navigate("/"); }
  }, [authUser, userId, navigate]);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const navClick = (id: TabKey) => setActiveTab(id);

  const renderMain = () => {
    switch (activeTab) {
      case "add":      return <Products/>;
       case "books":      return <Books/>;
      case "orders":   return <HostProductOrders/>;
      case "delivery": return <CommerceDeliveryZones/>;
      default:         return null;
    }
  };

  const initial = authUser?.email?.charAt(0)?.toUpperCase() ?? "A";
  const currentSidebarW = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W;

  /* ═══ RENDER ═══ */
  return (
    <div style={{
      minHeight: "100vh", background: HEART.bg,
      fontFamily: "'DM Sans',sans-serif",
      color: HEART.dark, display: "flex", position: "relative", overflow: "hidden",
    }}>
      <style>{CSS}</style>

      <img src={logo} alt="" className="page-watermark" />

      {/* ══════════════════════════
          SIDEBAR — desktop only
      ══════════════════════════ */}
      {!mobile && (
        <aside style={{
          width: currentSidebarW,
          background: HEART.panelBg,
          borderRight: `1px solid ${HEART.border}`,
          display: "flex", flexDirection: "column",
          position: "sticky",
          top: 0, height: "100vh", zIndex: 999,
          transition: "width .18s ease",
          boxShadow: "1px 0 0 rgba(242,169,62,.14)",
          flexShrink: 0,
          overflow: "hidden",
        }}>
          <img src={logo} alt="" className="sidebar-watermark" />

          <div style={{ height: 3, background: GRAD_RING, backgroundSize: "300% 100%",
            animation: "gradSpin 4s linear infinite", position: "relative", zIndex: 1 }}/>

          {/* logo block */}
          <div style={{
            padding: "18px 16px 16px",
            borderBottom: `1px solid ${HEART.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            position: "relative", zIndex: 1, background: HEART.panelBg,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <div style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
                <div style={{
                  position: "absolute", inset: -2, borderRadius: "50%",
                  background: GRAD_RING, backgroundSize: "300% 100%",
                  animation: "gradSpin 3s linear infinite", zIndex: 0,
                }}/>
                <div style={{
                  position: "relative", zIndex: 1, background: HEART.panelBg,
                  borderRadius: "50%", padding: 2, display: "inline-flex",
                }}>
                  <img src={logo} alt="Heartlas"
                    style={{ width: 28, height: 28, borderRadius: "50%",
                      objectFit: "cover", display: "block" }}/>
                </div>
              </div>

              {!collapsed && (
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 900, letterSpacing: 1.2,
                    background: GRAD_RING, backgroundSize: "300% 100%",
                    animation: "gradSpin 4s linear infinite",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                    fontFamily: "'Sora',sans-serif", whiteSpace: "nowrap",
                  }}>
                    HEARTLAS
                  </div>
                  <div style={{ fontSize: 8, color: HEART.textFaint,
                    letterSpacing: "0.18em", marginTop: 1 }}>
                    SELLER STUDIO
                  </div>
                </div>
              )}
            </div>

            <button
              className="sd-collapse-toggle"
              onClick={() => setCollapsed((p) => !p)}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
            </button>
          </div>

          {/* navigation */}
          <nav style={{ flex: 1, overflowY: "auto", padding: "12px 10px", position: "relative", zIndex: 1 }}>
            {NAV.map(({ id, label, icon: Icon }) => {
              const on = activeTab === id;
              return (
                <div key={id} style={{ marginBottom: 4 }}>
                  <button
                    className={`sd-nav${on ? " on" : ""}${collapsed ? " collapsed" : ""}`}
                    onClick={() => navClick(id)}
                    title={label}
                  >
                    <div className="sd-navicon">
                      <Icon size={16} color={on ? "#fff" : HEART.red} strokeWidth={on ? 2 : 1.7}/>
                    </div>
                    {!collapsed && (
                      <span style={{
                        fontSize: 13.5, fontWeight: on ? 800 : 600,
                        color: on ? "#fff" : HEART.dark,
                        flex: 1, letterSpacing: on ? -0.1 : 0,
                      }} className="sd-navlabel">
                        {label}
                      </span>
                    )}
                    {on && !collapsed && (
                      <div style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: "#fff", flexShrink: 0,
                        animation: "pulseDot 2s infinite",
                      }}/>
                    )}
                  </button>
                </div>
              );
            })}
          </nav>

          {/* sidebar footer */}
          <div style={{ padding: "14px 16px",
            borderTop: `1px solid ${HEART.border}`, position: "relative", zIndex: 1, background: HEART.panelBg,
            display: "flex", flexDirection: "column", gap: 8 }}>
            {!collapsed && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                  background: GRAD_H, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: "#fff" }}>{initial}</span>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: HEART.dark,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {authUser?.firstName || authUser?.displayName || "Seller"}
                  </div>
                  <div style={{ fontSize: 9, color: HEART.textFaint,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {authUser?.email || ""}
                  </div>
                </div>
              </div>
            )}
            <button className={`sd-logout${collapsed ? " collapsed" : ""}`}
              onClick={() => navigate("/logout")} title="Log out">
              <span style={{ display: "inline-flex", transform: "rotate(45deg)", fontSize: 15, lineHeight: 1 }}>+</span>
              {!collapsed && <span>Log out</span>}
            </button>
          </div>
        </aside>
      )}

      {/* ══════════════════════════
          MAIN CONTENT
      ══════════════════════════ */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, position: "relative", zIndex: 1 }}>

        {/* topbar */}
        <header style={{
          height: 56, background: HEART.headerBg,
          backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${HEART.border}`,
          display: "flex", alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          position: "sticky", top: 0, zIndex: 100, flexShrink: 0,
          boxShadow: "0 1px 8px rgba(27,14,6,.05)",
        }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2,
            background: GRAD_RING, backgroundSize: "300% 100%",
            animation: "gradSpin 4s linear infinite" }}/>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {mobile && (
              <div style={{ position: "relative", display: "inline-flex" }}>
                <div style={{
                  position: "absolute", inset: -2, borderRadius: "50%",
                  background: GRAD_RING, backgroundSize: "300% 100%",
                  animation: "gradSpin 3s linear infinite", zIndex: 0,
                }}/>
                <div style={{
                  position: "relative", zIndex: 1, background: HEART.panelBg,
                  borderRadius: "50%", padding: 2, display: "inline-flex",
                }}>
                  <img src={logo} alt="Heartlas"
                    style={{ width: 24, height: 24, borderRadius: "50%",
                      objectFit: "cover", display: "block" }}/>
                </div>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 3, height: 18, borderRadius: 99,
                background: GRAD_H }}/>
              <h1 style={{ fontSize: 16, fontWeight: 800, color: HEART.dark,
                letterSpacing: -0.2, fontFamily: "'Sora',sans-serif" }}>
                {NAV.find(n => n.id === activeTab)?.label ?? "Dashboard"}
              </h1>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "4px 10px", borderRadius: 999,
              background: "rgba(31,170,89,.08)",
              border: "1px solid rgba(31,170,89,.22)",
            }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%",
                background: HEART.success, animation: "pulseDot 2s infinite" }}/>
              <span style={{ fontSize: 9, fontWeight: 800,
                color: HEART.success, letterSpacing: "0.10em" }}>LIVE</span>
            </div>

            <div style={{
              width: 30, height: 30, borderRadius: "50%",
              background: GRAD_H,
              display: "flex", alignItems: "center",
              justifyContent: "center", cursor: "pointer",
              boxShadow: "0 3px 10px rgba(200,32,46,.28)",
            }}>
              <span style={{ fontSize: 11, fontWeight: 900, color: "#fff" }}>{initial}</span>
            </div>
          </div>
        </header>

        {/* page content */}
        <main style={{
          flex: 1, overflowY: "auto", background: "transparent",
          paddingBottom: mobile ? TABBAR_H + 12 : 0,
        }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: [0.2, 0, 0.1, 1] }}
              style={{ minHeight: "100%" }}
            >
              {renderMain()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* ══════════════════════════
          MOBILE BOTTOM TAB BAR
      ══════════════════════════ */}
      {mobile && (
        <div className="sd-tabbar">
          {NAV.map(({ id, label, icon: Icon }) => {
            const on = activeTab === id;
            return (
              <button
                key={id}
                className={`sd-tab${on ? " active" : ""}`}
                onClick={() => navClick(id)}
              >
                {on && <div className="sd-tab-dot" />}
                <div className="sd-tab-iconwrap">
                  <Icon size={18} color={on ? HEART.red : HEART.textFaint} strokeWidth={on ? 2 : 1.7} />
                </div>
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SellerDashboard;